import { useMemo } from 'react';
import type { CoreEmployee, EmployeePerformance, UserLabel } from '@/lib/zifekCore';
import { downloadCSV } from '@/lib/csv';

interface Props {
  employees: CoreEmployee[];
  performance?: Record<number, EmployeePerformance>;
  users: Record<number, UserLabel>;
  currency: string;
}

interface Row {
  employee: CoreEmployee;
  label: string;
  orders: number;
  sales: number;
  commission: number;
  target: number;
  progress: number | null;
}

/**
 * Suivi de performance par employé : objectifs de vente, classement des top
 * vendeurs, commissions cumulées et export CSV. Les ventes proviennent de
 * l'attribution des commandes (commandes.core_employee_id).
 */
export default function EmployeePerformancePanel({ employees, performance, users, currency }: Props) {
  const rows = useMemo<Row[]>(() => {
    return employees.map((e) => {
      const perf = performance?.[e.id];
      const label = (e.user_id && users[e.user_id]?.name) || e.job_title || e.email || `Employé #${e.id}`;
      const sales = perf?.sales || 0;
      const target = Number(e.sales_target ?? 0) || 0;
      return {
        employee: e,
        label,
        orders: perf?.orders || 0,
        sales,
        commission: perf?.commission || 0,
        target,
        progress: target > 0 ? Math.min(100, Math.round((sales / target) * 100)) : null,
      };
    });
  }, [employees, performance, users]);

  const ranked = useMemo(
    () => [...rows].sort((a, b) => b.sales - a.sales).filter((r) => r.orders > 0),
    [rows],
  );

  const totals = useMemo(() => ({
    sales: rows.reduce((s, r) => s + r.sales, 0),
    commission: rows.reduce((s, r) => s + r.commission, 0),
    orders: rows.reduce((s, r) => s + r.orders, 0),
    sellers: ranked.length,
  }), [rows, ranked]);

  const withTarget = rows.filter((r) => r.target > 0);

  const exportCsv = () => {
    const headers = ['Employé', 'Poste', 'Ventes', 'Chiffre d’affaires', 'Objectif', 'Progression %', 'Commission'];
    const data = [...rows]
      .sort((a, b) => b.sales - a.sales)
      .map((r) => [
        r.label,
        r.employee.job_title || '',
        String(r.orders),
        String(Math.round(r.sales * 100) / 100),
        String(r.target),
        r.progress != null ? String(r.progress) : '',
        String(Math.round(r.commission * 100) / 100),
      ]);
    downloadCSV('performance-employes.csv', headers, data);
  };

  const medal = (i: number) => (i === 0 ? 'ri-medal-fill text-amber-500' : i === 1 ? 'ri-medal-fill text-foreground-400' : i === 2 ? 'ri-medal-fill text-amber-700' : null);

  return (
    <div className="mb-6 space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Chiffre d’affaires attribué', value: totals.sales.toLocaleString(), suffix: currency, icon: 'ri-line-chart-line', tone: 'primary' },
          { label: 'Commissions cumulées', value: totals.commission.toLocaleString(), suffix: currency, icon: 'ri-hand-coin-line', tone: 'accent' },
          { label: 'Ventes attribuées', value: String(totals.orders), suffix: '', icon: 'ri-shopping-bag-3-line', tone: 'secondary' },
          { label: 'Vendeurs actifs', value: String(totals.sellers), suffix: '', icon: 'ri-user-star-line', tone: 'foreground' },
        ].map((k) => (
          <div key={k.label} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${
              k.tone === 'primary' ? 'bg-primary-100 text-primary-700'
                : k.tone === 'accent' ? 'bg-accent-100 text-accent-900'
                : k.tone === 'secondary' ? 'bg-secondary-100 text-secondary-700'
                : 'bg-foreground-100 text-foreground-700'
            }`}>
              <i className={`${k.icon} text-lg`}></i>
            </div>
            <div className="text-lg font-bold font-heading text-foreground-950">
              {k.value}{k.suffix ? <span className="text-xs text-foreground-500 ml-1">{k.suffix}</span> : null}
            </div>
            <div className="text-xs text-foreground-500">{k.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-background-200/70">
          <h4 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
            <i className="ri-trophy-line text-accent-500"></i> Top vendeurs & objectifs
          </h4>
          <button
            onClick={exportCsv}
            disabled={rows.length === 0}
            className="flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer disabled:opacity-50"
          >
            <i className="ri-download-2-line"></i> Exporter CSV
          </button>
        </div>

        {ranked.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <i className="ri-bar-chart-grouped-line text-2xl text-foreground-300"></i>
            <p className="text-sm text-foreground-500 mt-2">Aucune vente attribuée pour l’instant.</p>
            <p className="text-xs text-foreground-400 mt-1">Attribuez une commande à un employé depuis la page Commandes pour alimenter le classement.</p>
          </div>
        ) : (
          <div className="divide-y divide-background-200/60">
            {ranked.slice(0, 8).map((r, i) => (
              <div key={r.employee.id} className="flex items-center gap-3 px-4 py-3">
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-background-100 text-xs font-bold text-foreground-600 flex-shrink-0">
                  {medal(i) ? <i className={`${medal(i)} text-base`}></i> : i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground-900 truncate">{r.label}</span>
                    {r.employee.job_title && <span className="text-[10px] text-foreground-400 truncate hidden sm:inline">· {r.employee.job_title}</span>}
                  </div>
                  <div className="text-[11px] text-foreground-500">
                    {r.orders} vente{r.orders > 1 ? 's' : ''} · <span className="text-primary-700 font-semibold">{r.sales.toLocaleString()} {currency}</span> · commission {r.commission.toLocaleString()} {currency}
                  </div>
                </div>
                {r.progress != null && (
                  <div className="w-28 flex-shrink-0 hidden sm:block">
                    <div className="flex items-center justify-between text-[10px] mb-1">
                      <span className="text-foreground-400">Objectif {r.target.toLocaleString()}</span>
                      <span className={`font-semibold ${r.progress >= 100 ? 'text-accent-700' : 'text-foreground-600'}`}>{r.progress}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-background-200/70 overflow-hidden">
                      <div className={`h-full rounded-full ${r.progress >= 100 ? 'bg-accent-500' : 'bg-primary-500'}`} style={{ width: `${r.progress}%` }}></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {withTarget.length > 0 && (
          <div className="px-4 py-2.5 border-t border-background-200/70 text-[11px] text-foreground-500 flex items-center gap-1.5">
            <i className="ri-information-line"></i>
            Progression vers l’objectif de vente défini sur chaque fiche employé.
          </div>
        )}
      </div>
    </div>
  );
}